import { Directive, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NgControl } from '@angular/forms';

/**
 * Estado visual de un campo del design system. `normal` es el `default` de
 * Figma, renombrado porque `default` es palabra reservada en el `@switch` de las
 * plantillas.
 */
export type EstadoCampo = 'normal' | 'error' | 'exito' | 'deshabilitado';

/**
 * Base de los campos de formulario del design system (`app-input-texto`,
 * `app-select`).
 *
 * Concentra lo que en el backoffice está copiado en cada componente: el enganche
 * con `ReactiveForms` (`ControlValueAccessor`) y el estado visual derivado del
 * propio control. Los componentes que la extienden sólo ponen su plantilla y sus
 * entradas.
 *
 * A diferencia del backoffice, el estado **no** es una entrada: lo calcula el
 * campo mirando su control, así que la pantalla no tiene que pasarle un
 * `[status]` calculado a mano (`select.component.ts` sí lo pedía).
 *
 * Va decorada con `@Directive()` sin selector porque usa `inject()` y
 * `ngOnInit`: es lo que Angular pide para una clase base con features del
 * framework. No se declara en ningún `imports`.
 */
@Directive()
export abstract class CampoFormulario implements ControlValueAccessor, OnInit {
  private readonly destroyRef = inject(DestroyRef);

  /**
   * El `valueAccessor` se asigna a mano en vez de proveer `NG_VALUE_ACCESSOR`:
   * el estado visual necesita leer el `NgControl` (dirty/touched/invalid) y
   * pedirlo por DI con ese provider declarado es una dependencia circular.
   * Mismo enfoque que `input-text.component.ts` del backoffice.
   */
  protected readonly ngControl = inject(NgControl, {
    self: true,
    optional: true,
  });

  private readonly _valor = signal('');
  private readonly _estado = signal<EstadoCampo>('normal');

  /** Valor vigente, venga del formulario o de lo que tipeó el usuario. */
  readonly valor = this._valor.asReadonly();

  /** Estado visual del campo (bordes y colores del design system). */
  readonly estado = this._estado.asReadonly();

  private deshabilitadoPorForm = false;
  private alCambiar: (valor: string) => void = () => {};
  private alTocar: () => void = () => {};

  constructor() {
    if (this.ngControl) this.ngControl.valueAccessor = this;
  }

  ngOnInit(): void {
    // En el constructor `NgControl.control` todavía puede ser null: la directiva
    // (`formControlName`) lo enlaza en su primer `ngOnChanges`. `events` cubre de
    // una vez los cambios de valor, estado, pristine, touched y el reset — en el
    // backoffice esto son dos suscripciones separadas.
    this.ngControl?.control?.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.recalcularEstado());
  }

  // ── ControlValueAccessor ──────────────────────────────────────────────────

  writeValue(valor: unknown): void {
    this._valor.set(valor == null ? '' : String(valor));
  }

  registerOnChange(fn: (valor: string) => void): void {
    this.alCambiar = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.alTocar = fn;
  }

  setDisabledState(deshabilitado: boolean): void {
    this.deshabilitadoPorForm = deshabilitado;
    this.recalcularEstado();
  }

  // ── Para las plantillas de los campos ─────────────────────────────────────

  /** Lo que el usuario acaba de tipear o elegir. */
  protected escribir(valor: string): void {
    this._valor.set(valor);
    this.alCambiar(valor);
  }

  /** El campo perdió el foco: queda tocado y se revisa su estado. */
  protected tocar(): void {
    this.alTocar();
    this.recalcularEstado();
  }

  private recalcularEstado(): void {
    if (this.deshabilitadoPorForm) {
      this._estado.set('deshabilitado');
      return;
    }

    const control = this.ngControl;
    // Un campo que nadie editó ni visitó no muestra validación: abrir la
    // pantalla no tiene que pintar de rojo los cuatro campos vacíos.
    if (!control || (!control.dirty && !control.touched)) {
      this._estado.set('normal');
      return;
    }

    this._estado.set(control.invalid ? 'error' : 'exito');
  }
}
